// A change laid over a system's rules (issue 1136, the merge of issue 1310): the site's `scripts/mrcs/rules-overlay.js`
// ported, pure, so the tabletop lays a DM's ruling over the rules it already plays by (the system's, or the borrowed
// system's, with the table's homebrew merged in by the site) exactly as the site lays a homebrew layer over a system.
// The merge is the site's, deliberately small (docs/MRCS/combat-rules.md, "Homebrew overrides"):
//
//   · an object merges by key, recursively;
//   · a value of `null` takes the key out (`"critical": null` turns criticals off);
//   · a list whose entries each carry a `key` merges by that key: an entry of the overlay replaces the base entry of the
//     same key WHOLE, or is added at the end; `{ "key": "x", "remove": true }` takes the base entry out;
//   · any other list (natural results, initiative ties, trades) and every scalar replaces the base's.
//
// The site grades the merged result by its validators; the table grades it by `parseSystemRules`, the strict reader
// every rule the table plays by already passes through (`foldOverlay`): a part the overlay leaves unreadable refuses it.
import { parseCombatRules, parseStacking, parseUnits } from './system.js';
const isObject = (v) => v !== null && typeof v === 'object' && !Array.isArray(v);
const clone = (v) => (v === undefined ? v : JSON.parse(JSON.stringify(v)));
/** The ceiling on one overlay, in characters of JSON (the site's is far above any real override; a ruling is small). */
export const MAX_OVERLAY_CHARS = 4000;
/** A list merged by key: every entry an object with a string `key`. */
const isKeyedList = (v) => Array.isArray(v) && v.length > 0 && v.every((e) => isObject(e) && typeof e.key === 'string' && e.key.length > 0);
/** `over` merged over `base` (neither changed), calling `mark(path)` for every value `over` set, took out or added. */
function merge(base, over, path, mark) {
    if (over === undefined)
        return clone(base);
    if (over === null) {
        mark(path);
        return undefined;
    }
    if (isObject(over)) {
        const out = isObject(base) ? clone(base) : {};
        for (const [k, v] of Object.entries(over)) {
            const next = merge(out[k], v, `${path}/${k}`, mark);
            if (next === undefined)
                delete out[k];
            else
                out[k] = next;
        }
        return out;
    }
    if (isKeyedList(over) && (base === undefined || isKeyedList(base))) {
        const out = clone(base) ?? [];
        for (const e of over) {
            const at = out.findIndex((x) => x.key === e.key);
            mark(`${path}/${e.key}`);
            if (e.remove === true) {
                if (at >= 0)
                    out.splice(at, 1);
                continue;
            }
            if (at >= 0)
                out[at] = clone(e);
            else
                out.push(clone(e));
        }
        return out.length ? out : undefined;
    }
    mark(path);
    return clone(over);
}
export function rulesPart(rules) {
    const f = isObject(rules) ? rules : {};
    return { ...(isObject(f.combat) ? { combat: f.combat } : {}), ...(isObject(f.effects) ? { effects: f.effects } : {}) };
}
/**
 * The base rules with an overlay merged over them, as `{ combat, effects }` (each undefined when there is none).
 * `onSet(path)` is told each path the overlay set.
 */
export function mergeRules(base, overlay, onSet = () => { }) {
    const b = rulesPart(base);
    const o = isObject(overlay) ? overlay : {};
    const combat = merge(b.combat, o.combat, '/combat', onSet);
    const effects = merge(b.effects, o.effects, '/effects', onSet);
    return { ...(combat !== undefined ? { combat } : {}), ...(effects !== undefined ? { effects } : {}) };
}
/**
 * The parts of rules that are present and cannot be read: where `parseSystemRules` would read a part the data has as
 * null (or a list of it as empty, or drop a word). `parseCombatRules` reads each part on its own, so a part is the unit.
 */
export function brokenParts(rules) {
    const out = [];
    const c = rules.combat;
    if (c !== undefined) {
        const parsed = parseCombatRules(c);
        if (!parsed)
            out.push('combat');
        else {
            for (const k of ['attack', 'actions', 'saves', 'initiative', 'distance', 'areas'])
                if (c[k] !== undefined && parsed[k] === null)
                    out.push(`combat.${k}`);
            for (const k of ['defences', 'concealment'])
                if (Array.isArray(c[k]) && c[k].length && !parsed[k].length)
                    out.push(`combat.${k}`);
            if (c.damage !== undefined && parsed.minimumDamage === null && isObject(c.damage) && c.damage.minimum !== undefined)
                out.push('combat.damage');
            if (isObject(c.damage) && !parsed.damageRules && ['types', 'attack_types', 'reduction', 'adjust', 'temporary', 'weapon_stat', 'spell_attr'].some((k) => c.damage[k] !== undefined))
                out.push('combat.damage');
            for (const k of ['outcomes', 'labels']) {
                const raw = c[k];
                if (raw !== undefined && (!isObject(raw) || Object.keys(raw).length !== Object.keys(parsed[k]).length))
                    out.push(`combat.${k}`);
            }
        }
    }
    const fx = rules.effects ?? {};
    if (fx.stacking !== undefined && fx.stacking !== null && parseStacking(fx.stacking) === null)
        out.push('stacking');
    if (fx.units !== undefined && fx.units !== null && parseUnits(fx.units) === null)
        out.push('units');
    return out;
}
/**
 * The shape an overlay must have on its own: an object of `combat` and `effects` (each an object, or null), no other
 * key, a removal naming only its key, and small. Null when it is fine, else why in words.
 */
export function overlayShapeProblem(v) {
    if (!isObject(v))
        return 'A rule change is an object of combat and effects.';
    for (const k of Object.keys(v))
        if (k !== 'combat' && k !== 'effects')
            return `A rule change changes combat and effects, not "${k.slice(0, 40)}".`;
    for (const k of ['combat', 'effects'])
        if (v[k] !== undefined && v[k] !== null && !isObject(v[k]))
            return `${k} is an object, or null for none.`;
    if (v.combat === undefined && v.effects === undefined)
        return 'A rule change changes something.';
    let json;
    try {
        json = JSON.stringify(v);
    }
    catch {
        return 'A rule change is plain data.';
    }
    if (json.length > MAX_OVERLAY_CHARS)
        return `A rule change is at most ${MAX_OVERLAY_CHARS} characters.`;
    let bad = null;
    const walk = (x, depth) => {
        if (bad)
            return;
        if (depth > 12) {
            bad = 'A rule change is nested too deeply.';
            return;
        }
        if (Array.isArray(x)) {
            for (const e of x) {
                if (isObject(e) && e.remove !== undefined) {
                    if (e.remove !== true)
                        bad = 'remove is true, or left out.';
                    else if (Object.keys(e).some((k) => k !== 'key' && k !== 'remove'))
                        bad = 'An entry taken out names only its key.';
                    else if (typeof e.key !== 'string' || !e.key)
                        bad = 'An entry taken out names its key.';
                }
                walk(e, depth + 1);
            }
        }
        else if (isObject(x))
            for (const e of Object.values(x))
                walk(e, depth + 1);
        else if (typeof x === 'number' && !Number.isFinite(x))
            bad = 'A rule change holds plain numbers.';
    };
    walk(v, 0);
    return bad;
}
/**
 * The registry's rules (`{ combat, effects: { stacking, units }, sources }`) with one overlay laid over them, or null
 * when the overlay would leave a part that could be read unreadable (it is passed over whole, as a homebrew layer whose
 * rules would break the stack is). Each path it set names `source` in `sources`, taking over any lower path inside it.
 */
export function foldOverlay(registry, overlay, source) {
    const reg = isObject(registry) ? registry : {};
    const fx = isObject(reg.effects) ? reg.effects : {};
    const base = {
        ...(isObject(reg.combat) ? { combat: reg.combat } : {}),
        effects: { ...(fx.stacking != null ? { stacking: fx.stacking } : {}), ...(fx.units != null ? { units: fx.units } : {}) },
    };
    const set = [];
    const next = mergeRules(base, overlay, (p) => set.push(p));
    const before = new Set(brokenParts(base));
    if (brokenParts(next).some((p) => !before.has(p)))
        return null;
    const sources = isObject(reg.sources) ? { ...reg.sources } : {};
    for (const p of set) {
        for (const k of Object.keys(sources))
            if (k.startsWith(`${p}/`))
                delete sources[k];
        sources[p] = { ...source };
    }
    const nfx = next.effects ?? {};
    // A system's rests (issue 1482) are no ruling's to change: they ride through as the registry gave them.
    return { rules: { combat: next.combat ?? null, effects: { stacking: nfx.stacking ?? null, units: nfx.units ?? null }, ...(reg.rest != null ? { rest: reg.rest } : {}), sources }, set };
}
//# sourceMappingURL=overlay.js.map